import {
  Group, BoxGeometry, PlaneGeometry, EdgesGeometry, BufferGeometry,
  BufferAttribute, LineSegments, LineBasicMaterial, Mesh, MeshBasicMaterial,
  MeshStandardMaterial, AmbientLight, DirectionalLight, PointLight, Color,
} from 'three';

// Pure helpers — exported so tests don't need to stub three.js.
export function columnScaleFromBass(bassSpring) {
  return 1.0 + bassSpring * 0.15;
}

export function emissiveFromAudio(trebleSpring, beatPulse) {
  return Math.min(1.0, 0.3 + trebleSpring * 0.5 + beatPulse * 0.4);
}

export function ambientIntensityFromOnset(onsetPulse) {
  return 0.5 + onsetPulse * 0.5;
}

export function opacityFromSection(section) {
  return section === 'silence' ? 0.3 : 1.0;
}

export function altarEmissiveFromBass(bassSpring, beatPulse) {
  return Math.min(1.5, 0.5 + bassSpring * 0.5 + beatPulse * 0.5);
}

export function altarLightIntensityFromBeat(beatPulse) {
  return 1.5 + beatPulse * 3.0;
}

export function archOpacityFromMids(midsSpring) {
  return 0.5 + midsSpring * 0.5;
}

export function columnTintFromSection(section) {
  if (section === 'bass-heavy') return { h: 0.05 };
  if (section === 'treble-heavy') return { h: -0.05 };
  return { h: 0 };
}

const WIRE_COLOR = 0x66aaff;
const FILL_COLOR = 0x0a1428;
const FLOOR_FILL_COLOR = 0x050a18;
const ALTAR_COLOR = 0x102040;
const ALTAR_EMISSIVE = 0x66aaff;
const WIRE_BASE_OPACITY = 0.9;
const FILL_BASE_OPACITY = 0.15;
// 60s per full revolution.
const ROTATION_RAD_PER_SEC = (Math.PI * 2) / 60;

// Z positions for the 4 columns along each side of the nave.
const COLUMN_Z = [-7, -3, 1, 5];
const COLUMN_TOP_Y = 4;
const ARCH_SEGMENTS = 16;
const ALTAR_Z = -9;

export class Cathedral {
  constructor() {
    this.root = new Group();
    this.columns = [];
    this.arches = [];
    this.altarMesh = null;
    this.altarLight = null;
    // Reusable Color scratch to avoid per-frame allocation in update().
    this._tintColor = new Color();
    this._baseColumnColor = new Color(WIRE_COLOR);

    this.ambientLight = new AmbientLight(0x1a2a4a, 0.25);
    this.keyLight = new DirectionalLight(0xffffff, 0.6);
    this.keyLight.position.set(0, 6, 6);
    this._build();
  }

  _build() {
    const floor = this._wirePlane(8, 22, -0.01);
    this.root.add(floor);
    const ceiling = this._wirePlane(8, 22, 5);
    this.root.add(ceiling);
    this._buildFloorFill();

    for (const side of [-1, 1]) {
      for (const z of COLUMN_Z) {
        const col = this._column();
        col.position.set(side * 2, 2, z);
        this.root.add(col);
        this.columns.push(col);
      }
    }

    this._buildArches();
    this._buildAltar();
  }

  _column() {
    const g = new Group();
    const box = new BoxGeometry(0.4, 4, 0.4);
    const fillMat = new MeshBasicMaterial({
      color: FILL_COLOR, transparent: true, opacity: FILL_BASE_OPACITY,
    });
    const fill = new Mesh(box, fillMat);
    const wireMat = new LineBasicMaterial({
      color: WIRE_COLOR, transparent: true, opacity: WIRE_BASE_OPACITY,
    });
    const wire = new LineSegments(new EdgesGeometry(box), wireMat);
    g.add(fill);
    g.add(wire);
    g.userData.fillMat = fillMat;
    g.userData.wireMat = wireMat;
    return g;
  }

  _wirePlane(w, d, y) {
    const geo = new PlaneGeometry(w, d);
    const edges = new EdgesGeometry(geo);
    const mat = new LineBasicMaterial({
      color: WIRE_COLOR, transparent: true, opacity: 0.4,
    });
    const ls = new LineSegments(edges, mat);
    ls.rotation.x = -Math.PI / 2;
    ls.position.y = y;
    geo.dispose();
    ls.userData.edges = edges;
    ls.userData.mat = mat;
    return ls;
  }

  _buildFloorFill() {
    const geo = new PlaneGeometry(8, 22);
    const mat = new MeshBasicMaterial({
      color: FLOOR_FILL_COLOR, transparent: true, opacity: 0.4,
    });
    const plane = new Mesh(geo, mat);
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = -0.02;
    this.root.add(plane);
  }

  _buildArches() {
    // One arch spans between adjacent columns on the same side; 3 per side.
    for (const side of [-1, 1]) {
      for (let i = 0; i < COLUMN_Z.length - 1; i++) {
        const z0 = COLUMN_Z[i];
        const z1 = COLUMN_Z[i + 1];
        const arch = this._arch(side * 2, z0, z1);
        this.root.add(arch);
        this.arches.push(arch);
      }
    }
  }

  _arch(x, z0, z1) {
    // Semicircular arc in the y-z plane between (x,top,z0) and (x,top,z1).
    const zMid = (z0 + z1) / 2;
    const radius = Math.abs(z1 - z0) / 2;
    const positions = new Float32Array(ARCH_SEGMENTS * 2 * 3);
    for (let i = 0; i < ARCH_SEGMENTS; i++) {
      const t0 = i / ARCH_SEGMENTS;
      const t1 = (i + 1) / ARCH_SEGMENTS;
      const a0 = Math.PI * t0;
      const a1 = Math.PI * t1;
      const idx = i * 6;
      positions[idx] = x;
      positions[idx + 1] = COLUMN_TOP_Y + Math.sin(a0) * radius;
      positions[idx + 2] = zMid - Math.cos(a0) * radius;
      positions[idx + 3] = x;
      positions[idx + 4] = COLUMN_TOP_Y + Math.sin(a1) * radius;
      positions[idx + 5] = zMid - Math.cos(a1) * radius;
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(positions, 3));
    const mat = new LineBasicMaterial({
      color: WIRE_COLOR, transparent: true, opacity: 0.5,
    });
    const ls = new LineSegments(geo, mat);
    ls.userData.mat = mat;
    return ls;
  }

  _buildAltar() {
    const geo = new BoxGeometry(0.5, 2.5, 0.5);
    const mat = new MeshStandardMaterial({
      color: ALTAR_COLOR, emissive: ALTAR_EMISSIVE, emissiveIntensity: 0.5,
    });
    const mesh = new Mesh(geo, mat);
    mesh.position.set(0, 1.25, ALTAR_Z);
    this.root.add(mesh);
    this.altarMesh = mesh;

    this.altarLight = new PointLight(0x66aaff, 1.5, 12);
    this.altarLight.position.set(0, 2.5, ALTAR_Z);
    this.root.add(this.altarLight);
  }

  update(signals, elapsedSeconds) {
    this.root.rotation.y = elapsedSeconds * ROTATION_RAD_PER_SEC;

    const scaleY = columnScaleFromBass(signals.bassSpring);
    const emissive = emissiveFromAudio(signals.trebleSpring, signals.beatPulse);
    const opacityMul = opacityFromSection(signals.section);
    const tint = columnTintFromSection(signals.section);

    // Apply hue shift via HSL offset on a scratch color; lerp 15% off base.
    this._tintColor.copy(this._baseColumnColor);
    if (tint.h !== 0) this._tintColor.offsetHSL(tint.h, 0, 0);

    for (const col of this.columns) {
      col.scale.y = scaleY;
      col.userData.wireMat.opacity = WIRE_BASE_OPACITY * opacityMul * emissive;
      col.userData.fillMat.opacity = FILL_BASE_OPACITY * opacityMul * (0.5 + emissive * 0.5);
      col.userData.wireMat.color.copy(this._baseColumnColor).lerp(this._tintColor, 0.15);
    }

    const archOp = archOpacityFromMids(signals.midsSpring) * opacityMul;
    for (const arch of this.arches) {
      arch.userData.mat.opacity = archOp;
    }

    if (this.altarMesh) {
      this.altarMesh.material.emissiveIntensity = altarEmissiveFromBass(
        signals.bassSpring, signals.beatPulse,
      );
    }
    if (this.altarLight) {
      this.altarLight.intensity = altarLightIntensityFromBeat(signals.beatPulse);
    }

    this.ambientLight.intensity = ambientIntensityFromOnset(signals.onsetPulse);
  }

  dispose() {
    this.root.traverse((obj) => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) obj.material.dispose();
    });
  }
}
